import { z } from 'zod';
import { Id } from '../primitives.js';
import { Kopecks } from './finance.js';
import { Milliquantity } from './inventory.js';

/**
 * Procurement: поставщики и закупки (веха 3.8).
 *
 * Количество — милли-единицы, цена — копейки: тот же приём, что у склада,
 * несмотря на Decimal в спеке. Два представления одной величины однажды
 * разошлись бы на приёмке, которая создаёт партию через Inventory.
 *
 * Документация модуля: docs/03-modules/17-procurement.md
 */

export const PURCHASE_ORDER_STATUS = [
  'draft',
  'pending_approval',
  'approved',
  'rejected',
  'sent',
  'confirmed',
  'in_transit',
  'partially_received',
  'received',
  'closed',
  'cancelled',
] as const;
export const PurchaseOrderStatus = z.enum(PURCHASE_ORDER_STATUS);
export type PurchaseOrderStatus = z.infer<typeof PurchaseOrderStatus>;

export const PURCHASE_LINE_KIND = ['material', 'instrument_type', 'equipment'] as const;
export const PurchaseLineKind = z.enum(PURCHASE_LINE_KIND);
export type PurchaseLineKind = z.infer<typeof PurchaseLineKind>;

export const RECEIPT_ISSUE_KIND = [
  'short_delivery',
  'over_delivery',
  'expired',
  'damaged_packaging',
  'wrong_item',
] as const;
export const ReceiptIssueKind = z.enum(RECEIPT_ISSUE_KIND);
export type ReceiptIssueKind = z.infer<typeof ReceiptIssueKind>;

export const RECEIPT_ISSUE_RESOLUTION = ['backorder', 'charge_supplier', 'accept', 'write_off'] as const;
export const ReceiptIssueResolution = z.enum(RECEIPT_ISSUE_RESOLUTION);
export type ReceiptIssueResolution = z.infer<typeof ReceiptIssueResolution>;

export const PACKAGING_CONDITION = ['intact', 'damaged'] as const;
export const PackagingCondition = z.enum(PACKAGING_CONDITION);
export type PackagingCondition = z.infer<typeof PackagingCondition>;

export const Supplier = z.object({
  id: Id,
  name: z.string().min(1).max(200),
  inn: z.string().min(10).max(12).nullable(),
  categories: z.array(z.string().min(1).max(80)),
  leadTimeDays: z.number().int().min(0).max(365),
  paymentTerms: z.string().max(500).nullable(),
  comment: z.string().max(1000).nullable(),
  isActive: z.boolean(),
  rowVersion: z.number().int().nonnegative(),
});
export type Supplier = z.infer<typeof Supplier>;

export const SupplierContact = z.object({
  id: Id,
  supplierId: Id,
  name: z.string().min(1).max(120),
  role: z.string().max(80).nullable(),
  phone: z.string().max(32).nullable(),
  email: z.email().nullable(),
  rowVersion: z.number().int().nonnegative(),
});
export type SupplierContact = z.infer<typeof SupplierContact>;

export const CreateSupplierRequest = z.object({
  name: z.string().min(1).max(200),
  inn: z.string().min(10).max(12).nullable().default(null),
  categories: z.array(z.string().min(1).max(80)).default([]),
  leadTimeDays: z.number().int().min(0).max(365).default(5),
  paymentTerms: z.string().max(500).nullable().default(null),
  comment: z.string().max(1000).nullable().default(null),
});
export type CreateSupplierRequest = z.infer<typeof CreateSupplierRequest>;

export const UpdateSupplierRequest = z.object({
  name: z.string().min(1).max(200).optional(),
  inn: z.string().min(10).max(12).nullable().optional(),
  categories: z.array(z.string().min(1).max(80)).optional(),
  leadTimeDays: z.number().int().min(0).max(365).optional(),
  paymentTerms: z.string().max(500).nullable().optional(),
  comment: z.string().max(1000).nullable().optional(),
  isActive: z.boolean().optional(),
});
export type UpdateSupplierRequest = z.infer<typeof UpdateSupplierRequest>;

export const UpsertSupplierContactRequest = z.object({
  name: z.string().min(1).max(120),
  role: z.string().max(80).nullable().default(null),
  phone: z.string().max(32).nullable().default(null),
  email: z.email().nullable().default(null),
});
export type UpsertSupplierContactRequest = z.infer<typeof UpsertSupplierContactRequest>;

export const PurchaseOrderLine = z.object({
  id: Id,
  kind: PurchaseLineKind,
  itemId: Id,
  quantity: Milliquantity.positive(),
  unitPriceKopecks: Kopecks.nonnegative(),
  receivedQuantity: Milliquantity.nonnegative(),
});
export type PurchaseOrderLine = z.infer<typeof PurchaseOrderLine>;

export const PurchaseOrder = z.object({
  id: Id,
  number: z.string().min(1).max(40),
  supplierId: Id,
  authorId: Id,
  status: PurchaseOrderStatus,
  expectedAt: z.iso.datetime().nullable(),
  totalKopecks: Kopecks.nonnegative(),
  rejectReason: z.string().max(500).nullable(),
  sendMethod: z.string().max(80).nullable(),
  lines: z.array(PurchaseOrderLine),
  rowVersion: z.number().int().nonnegative(),
});
export type PurchaseOrder = z.infer<typeof PurchaseOrder>;

export const PurchaseOrderLineInput = z.object({
  kind: PurchaseLineKind,
  itemId: Id,
  quantity: Milliquantity.positive(),
  unitPriceKopecks: Kopecks.nonnegative(),
});
export type PurchaseOrderLineInput = z.infer<typeof PurchaseOrderLineInput>;

export const CreatePurchaseOrderRequest = z.object({
  supplierId: Id,
  expectedAt: z.iso.datetime().nullable().default(null),
  lines: z.array(PurchaseOrderLineInput).min(1),
});
export type CreatePurchaseOrderRequest = z.infer<typeof CreatePurchaseOrderRequest>;

export const UpdatePurchaseOrderRequest = z.object({
  supplierId: Id.optional(),
  expectedAt: z.iso.datetime().nullable().optional(),
  lines: z.array(PurchaseOrderLineInput).min(1).optional(),
});
export type UpdatePurchaseOrderRequest = z.infer<typeof UpdatePurchaseOrderRequest>;

export const PurchaseOrderChange = z.discriminatedUnion('action', [
  z.object({ action: z.literal('submit') }),
  z.object({
    action: z.literal('update'),
    supplierId: Id.optional(),
    expectedAt: z.iso.datetime().nullable().optional(),
    lines: z.array(PurchaseOrderLineInput).min(1).optional(),
  }),
  z.object({ action: z.literal('mark_confirmed'), expectedAt: z.iso.datetime() }),
  z.object({ action: z.literal('mark_in_transit') }),
]);
export type PurchaseOrderChange = z.infer<typeof PurchaseOrderChange>;

export const FromSuggestionsRequest = z.object({
  supplierId: Id,
  suggestionIds: z.array(Id).min(1),
});
export type FromSuggestionsRequest = z.infer<typeof FromSuggestionsRequest>;

export const SendOrderRequest = z.object({
  method: z.string().min(1).max(80),
});
export type SendOrderRequest = z.infer<typeof SendOrderRequest>;

export const RejectOrderRequest = z.object({
  reason: z.string().min(3).max(500),
});
export type RejectOrderRequest = z.infer<typeof RejectOrderRequest>;

export const CancelOrderRequest = z.object({
  reason: z.string().min(3).max(500),
});
export type CancelOrderRequest = z.infer<typeof CancelOrderRequest>;

export const GoodsReceiptLineInput = z.object({
  orderLineId: Id,
  quantity: Milliquantity.positive(),
  lotNumber: z.string().min(1).max(80).nullable().default(null),
  expiresAt: z.iso.datetime().nullable().default(null),
  packaging: PackagingCondition.default('intact'),
  /** Явное подтверждение приёмки просроченной партии — без него отказ. */
  acceptExpired: z.boolean().default(false),
  locationId: Id,
  /** Размер упаковки, которым вводили количество, — запомнится у материала. */
  packageSize: Milliquantity.positive().nullable().default(null),
});
export type GoodsReceiptLineInput = z.infer<typeof GoodsReceiptLineInput>;

export const CreateGoodsReceiptRequest = z.object({
  invoiceNumber: z.string().min(1).max(80).nullable().default(null),
  receivedAt: z.iso.datetime(),
  lines: z.array(GoodsReceiptLineInput).min(1),
});
export type CreateGoodsReceiptRequest = z.infer<typeof CreateGoodsReceiptRequest>;

export const ResolveReceiptIssueRequest = z.object({
  resolution: ReceiptIssueResolution,
  comment: z.string().max(500).nullable().default(null),
});
export type ResolveReceiptIssueRequest = z.infer<typeof ResolveReceiptIssueRequest>;

export const ReceiptIssue = z.object({
  id: Id,
  receiptId: Id,
  orderLineId: Id,
  kind: ReceiptIssueKind,
  quantity: Milliquantity.positive(),
  resolution: ReceiptIssueResolution.nullable(),
  resolvedBy: Id.nullable(),
  resolvedAt: z.iso.datetime().nullable(),
  comment: z.string().max(500).nullable(),
});
export type ReceiptIssue = z.infer<typeof ReceiptIssue>;

export const GoodsReceiptResult = z.object({
  id: Id,
  orderId: Id,
  rowVersion: z.number().int().nonnegative(),
  issues: z.array(ReceiptIssue),
});
export type GoodsReceiptResult = z.infer<typeof GoodsReceiptResult>;

export const ReorderSuggestion = z.object({
  id: Id,
  kind: PurchaseLineKind,
  itemId: Id,
  itemName: z.string(),
  supplierId: Id.nullable(),
  quantity: Milliquantity.nonnegative(),
  avgDaily: z.number().nonnegative(),
  available: Milliquantity.nonnegative(),
  inbound: Milliquantity.nonnegative(),
  daysToZero: z.number().nullable(),
  leadTimeDays: z.number().int().nonnegative(),
  reason: z.string(),
});
export type ReorderSuggestion = z.infer<typeof ReorderSuggestion>;

export const SupplierPricePoint = z.object({
  supplierId: Id,
  supplierName: z.string(),
  unitPriceKopecks: Kopecks.nonnegative(),
  orderedAt: z.iso.datetime(),
});
export type SupplierPricePoint = z.infer<typeof SupplierPricePoint>;
